import React from 'react';
import { Navigate } from 'react-router-dom';
import { SecurityDashboard } from './SecurityDashboard';
import { PrivacyDashboard } from './PrivacyDashboard';
import { HubDashboard } from './HubDashboard';
import { AdminDashboard } from './AdminDashboard';

/**
 * Thin space-aware switcher for the one shared /dashboard route. Reads the same localStorage
 * key App.tsx's space switcher writes (no Context/store — matches the established pattern, see
 * App.tsx's own comment on NimbusSpace), so this needs no prop from Layout. App.tsx keys its
 * <Outlet/> on space, so switching spaces while already sitting on /dashboard remounts this
 * component fresh — a plain read here would otherwise go stale until the next real navigation.
 *
 * Project has no dashboard of its own here: /tracker (Initiative Overview) already is one —
 * stat tiles, a timeline, an "All initiatives" table — for the space's main content, so
 * landing there instead of standing up a second, thinner summary avoids showing the same
 * initiative counts twice in two different-looking widgets.
 */
type NimbusSpace = 'security' | 'privacy' | 'admin' | 'hub' | 'project';
const SPACE_STORAGE_KEY = 'nimbusSpace';

export const DashboardPage: React.FC = () => {
  const space = (localStorage.getItem(SPACE_STORAGE_KEY) as NimbusSpace | null) || 'security';

  if (space === 'privacy') return <PrivacyDashboard />;
  if (space === 'hub') return <HubDashboard />;
  if (space === 'admin') return <AdminDashboard />;
  if (space === 'project') return <Navigate to="/tracker" replace />;
  return <SecurityDashboard />;
};
